import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import styles from "./StepShell.module.css";
import { API_BASE_URL } from "../../../Store/apiStore";
import decodeToken from "../../../lib/decodeToken";
import AnimatedButton from "../../AnimatedButton/AnimatedButton";
import { markSetupStep } from "../../../lib/setupScore";
import { trackEvent } from "../../../lib/analytics";

// GoogleAuthStep — wizard screen for Google OAuth (Calendar / Contacts /
// Profile scopes). Shared by PostPurchaseSetup AND GoLiveWizard so both
// flows render identical UI per the Figma "Let your agent work with
// Google" / "Thanks for providing access" frames.
//
// Initial (disconnected) view shows a red ✕ badge on every service
// card icon — they're not yet authorised. After the OAuth round-trip
// the success view sequentially reveals a green ✓ badge on each
// granted service. A timer auto-advances via onNext once all critical
// scopes land + the reveal completes.
//
// All requested scopes are configured on the backend's /connect
// endpoint — nothing scope-related is sent from the frontend.

// ── Scope catalogue ──────────────────────────────────────────────────
// Each visible service maps to one Google scope URL. We compare against
// the `connectedScopes` string Google returned to decide whether to
// render the granted tick or the empty circle.
//
// `critical: true` means the wizard's auto-advance only fires when
// THIS scope is in the granted set. If a critical scope is missing the
// step shows a "Re-grant access" warning instead of advancing — the
// agent literally can't do its job without it.
//
// We list ONLY 3 services per Figma — Calendar, Contacts, Profile.
// The legacy "Appointments" tile (calendar.events scope) was folded
// into Calendar's altScopes so the underlying permission still maps
// to a granted check even though it's not visually called out.
const SERVICES = [
  {
    key: "calendar",
    label: "Calendar",
    blurb:
      "Books, reschedules and reads events so the agent can answer availability questions.",
    color: "#4285F4",
    scope: "https://www.googleapis.com/auth/calendar",
    // calendar.events used to be its own "Appointments" service; per
    // Figma we hide that tile and roll its scope into Calendar's grant
    // check so the user sees ONE Calendar entry that covers both
    // reading and writing events.
    altScopes: ["https://www.googleapis.com/auth/calendar.events"],
    critical: true,
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="2" stroke="#4285F4" strokeWidth="2" />
        <path d="M3 9h18" stroke="#4285F4" strokeWidth="2" />
        <path d="M8 3v4M16 3v4" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" />
        <path d="M8 13l2.4 2.4L16 11" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    key: "contacts",
    label: "Contacts",
    blurb:
      "Recognizes callers for personal greetings and repeat customer recognition.",
    color: "#F59E0B",
    scope: "https://www.googleapis.com/auth/contacts.readonly",
    critical: false,
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="9" r="3.5" stroke="#F59E0B" strokeWidth="2" />
        <path d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    key: "profile",
    label: "Profile",
    blurb:
      "Identifies you so the agent signs sent invites/emails as the right person.",
    color: "#6524EB",
    // Google returns the OIDC-style "openid"/"profile" scope on consent;
    // either is enough to identify the user.
    scope: "profile",
    altScopes: ["openid", "https://www.googleapis.com/auth/userinfo.profile"],
    critical: false,
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="9" r="3.5" stroke="#6524EB" strokeWidth="2" />
        <path d="M5 20a7 7 0 0 1 14 0" stroke="#6524EB" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
];

// Sequential tick reveal cadence — each service card "checks in" 600ms
// after the previous one. Total reveal time = SERVICES.length * 600ms.
const TICK_REVEAL_INTERVAL_MS = 600;
// After all ticks settle, hold for this long before auto-advancing —
// gives the user a beat to read the green confirmation before we move
// them to the next step.
const POST_REVEAL_HOLD_MS = 1600;

// returnToPath — where the OAuth callback redirects the user back to
// after Google consent. Defaults to "/post-purchase-setup" so the
// PostPurchase wizard's resume flow is unchanged. GoLiveWizard passes
// "/go-live-setup".
//
// onSkip — optional. When provided, a small "Skip" link renders below
// the Continue button. PostPurchase doesn't pass it (Google connect
// there is mandatory). GoLive passes it because the user has already
// finished the core setup wizard at that point.
//
// expectReturnKey + lastStepKey — localStorage keys we set just before
// redirecting to Google so each host wizard can detect a successful
// round-trip on boot. Defaults to PostPurchase keys.
export default function GoogleAuthStep({
  isConnected = false,
  connectedEmail = "",
  connectedScopes = "",
  justReturnedFromGoogle = false,
  onNext,
  returnToPath = "/post-purchase-setup",
  onSkip,
  /*
   * Skip link visibility — explicit boolean. Earlier we gated on
   * `typeof onSkip === "function"`, which mis-fired in PostPurchase
   * because its wizard-wide onSkip prop is passed to every step via
   * StepComponent — meaning the link rendered there too even though
   * Google connect is mandatory in that flow. GoLive opts in by
   * passing BOTH `onSkip` and `showSkipLink={true}`.
   */
  showSkipLink = false,
  expectReturnKey = "postPurchaseSetup.expectGoogleReturn",
  lastStepKey = "postPurchaseSetup.lastStep",
  onBackToPrevious,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Sequential tick reveal — increments every TICK_REVEAL_INTERVAL_MS
  // while the success view is mounted. A given service card shows its
  // tick only when its index < revealedCount, so they "check in" top
  // to bottom instead of all popping at once.
  const [revealedCount, setRevealedCount] = useState(0);

  // Ref guards onNext from firing twice — strict-mode double-mount in
  // dev, or a re-render between the timer firing and the parent state
  // updating, could otherwise advance the step twice.
  const advancedRef = useRef(false);
  const busyTimeoutRef = useRef(null);

  // A cancelled Google redirect can restore this page from the browser's
  // back-forward cache with React state intact. Reset the in-flight button
  // and return GoLive to its persisted previous wizard step instead of
  // leaving "Opening Google…" disabled forever.
  useEffect(() => {
    const handlePageShow = (event) => {
      setBusy(false);
      if (busyTimeoutRef.current) {
        clearTimeout(busyTimeoutRef.current);
        busyTimeoutRef.current = null;
      }

      if (
        event.persisted &&
        typeof onBackToPrevious === "function" &&
        localStorage.getItem(expectReturnKey) === "true"
      ) {
        localStorage.removeItem(expectReturnKey);
        onBackToPrevious();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      if (busyTimeoutRef.current) clearTimeout(busyTimeoutRef.current);
    };
  }, [expectReturnKey, onBackToPrevious]);

  // Derive granted state from the space-separated scope string Google
  // returned in tokens.scope on the OAuth callback.
  const grantedSet = new Set((connectedScopes || "").split(/\s+/).filter(Boolean));
  const isGranted = (s) => {
    if (grantedSet.has(s.scope)) return true;
    if (Array.isArray(s.altScopes)) {
      return s.altScopes.some((alt) => grantedSet.has(alt));
    }
    return false;
  };
  const allCriticalGranted = SERVICES.filter((s) => s.critical).every(isGranted);

  // Sequential tick reveal — restart on each fresh entry into the
  // connected view (isConnected toggles false→true).
  useEffect(() => {
    if (!isConnected) {
      setRevealedCount(0);
      return;
    }
    setRevealedCount(0);
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      setRevealedCount(i);
      if (i >= SERVICES.length) clearInterval(interval);
    }, TICK_REVEAL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isConnected]);

  // Auto-advance after the sequential reveal finishes.
  //
  // Two host modes (gated by `showSkipLink` — same boolean that
  // controls Skip link visibility, since "skip allowed" implies
  // "Google is optional" implies "auto-advance lenient on partial
  // grant"):
  //   - PostPurchase (showSkipLink=false) — Google is mandatory,
  //     auto-advance only fires when ALL critical scopes granted.
  //   - GoLive (showSkipLink=true) — Google is recommended-but-
  //     optional. Auto-advance whenever connected, partial-grant OK.
  const isOptionalAuth = showSkipLink;
  useEffect(() => {
    if (!isConnected) return;
    if (!justReturnedFromGoogle) return;
    if (!isOptionalAuth && !allCriticalGranted) return;
    if (typeof onNext !== "function") return;
    if (advancedRef.current) return;

    /*
     * Setup Score: mark `contactsSynced` (10 pts) ONLY if the user
     * actually granted the contacts.readonly scope. Idempotent on
     * server, fire-and-forget on client.
     */
    const CONTACTS_SCOPE = "https://www.googleapis.com/auth/contacts.readonly";
    if (grantedSet.has(CONTACTS_SCOPE)) {
      markSetupStep("contactsSynced");
      // NOTE: spam_filtering_enabled (with contacts_synced_count) fires from
      // ContactsPage where the actual synced count is known — not here, since
      // granting the scope alone has no count yet.
    }

    // GA4: Google Calendar connected.
    const CALENDAR_SCOPES = [
      "https://www.googleapis.com/auth/calendar",
      "https://www.googleapis.com/auth/calendar.events",
    ];
    if (CALENDAR_SCOPES.some((s) => grantedSet.has(s))) {
      trackEvent("calendar_sync_completed", { source: "in_app" });
    }

    const totalWait =
      SERVICES.length * TICK_REVEAL_INTERVAL_MS + POST_REVEAL_HOLD_MS;
    const t = setTimeout(() => {
      advancedRef.current = true;
      onNext();
    }, totalWait);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, justReturnedFromGoogle, allCriticalGranted, connectedScopes, onNext]);

  // ── In-flight view — visitor just returned from Google but the
  // backend /calender-check is still resolving. ──────────────────────
  if (justReturnedFromGoogle && !isConnected) {
    return (
      <section className={styles.step} style={{ paddingTop: 32 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            padding: "60px 20px",
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              border: "4px solid #E0E5F2",
              borderTopColor: "#6524EB",
              borderRadius: "50%",
              animation: "googleAuthSpin 0.9s linear infinite",
            }}
          />
          <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
            Confirming your Google permissions…
          </p>
        </div>
        <style>{`@keyframes googleAuthSpin { to { transform: rotate(360deg); } }`}</style>
      </section>
    );
  }

  /*
   * Reusable service-card row. Renders the colour icon + label + blurb
   * + a small status badge in the top-right of the icon tile:
   *   - statusBadge="cross"   → red ✕ (initial / not connected)
   *   - statusBadge="check"   → green ✓ (connected + granted, revealed)
   *   - statusBadge="pending" → empty (not yet revealed in animation)
   */
  const renderServiceCard = (s, statusBadge, idx) => {
    const isCheck = statusBadge === "check";
    return (
      <div
        key={s.key}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          padding: "12px 14px",
          background: isCheck ? "#F0FDF4" : "#fff",
          border: isCheck ? "1px solid #10B981" : "1px solid #E0E5F2",
          borderRadius: 14,
          transition: "background 0.35s ease, border-color 0.35s ease",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 44,
            height: 44,
            borderRadius: 10,
            background: `${s.color}14`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {s.icon}
          {/*
             Status badge in the top-right corner of the icon tile.
             - cross: solid red circle with white ✕
             - check: solid green circle with white ✓ (animated pop in)
             - pending: hidden (used during the sequential reveal so
               cards appear "blank" until their turn comes up)
          */}
          {statusBadge === "cross" && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: -5,
                right: -5,
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "#DC2626",
                border: "2px solid #fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="9" height="9" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4 4L12 12M12 4L4 12"
                  stroke="#fff"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          )}
          {statusBadge === "check" && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: -5,
                right: -5,
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "#10B981",
                border: "2px solid #fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "googleAuthBadgePop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
            >
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3.5 8L6.5 11L12.5 5"
                  stroke="#fff"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "#24252c",
              marginBottom: 2,
            }}
          >
            {s.label}
          </div>
          <div
            style={{
              fontSize: 13,
              color: "#6b7280",
              lineHeight: 1.4,
            }}
          >
            {s.blurb}
          </div>
        </div>
      </div>
    );
  };

  /*
   * Reusable 2-line title — light first line + bold second per Figma.
   * Same treatment in both connected and disconnected states.
   */
  const renderTitle = (lightLine, boldLine) => (
    <div style={{ textAlign: "center", padding: "0 8px", lineHeight: 1.1 }}>
      <h1
        style={{
          fontSize: 32,
          fontWeight: 400,
          color: "#24252c",
          margin: 0,
          lineHeight: 1.1,
          fontFamily: "inherit",
        }}
      >
        {lightLine}
      </h1>
      <h1
        style={{
          fontSize: 32,
          fontWeight: 800,
          color: "#24252c",
          margin: 0,
          marginTop: 2,
          lineHeight: 1.1,
          fontFamily: "inherit",
        }}
      >
        {boldLine}
      </h1>
    </div>
  );

  // ── Connected view — service cards with sequential green-tick reveal ──
  if (isConnected) {
    return (
      <section className={styles.step} style={{ paddingTop: 32 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 8,
            marginBottom: 18,
          }}
        >
          <div
            style={{
              position: "relative",
              width: 76,
              height: 76,
              borderRadius: "50%",
              background: "#fff",
              border: "1.5px solid #10B981",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
              <path
                d="M46 24.5c0-1.6-.1-3.2-.4-4.7H24v8.9h12.4c-.5 2.9-2.1 5.3-4.6 7v5.8h7.4c4.3-4 6.8-9.9 6.8-17z"
                fill="#4285F4"
              />
              <path
                d="M24 47c6.2 0 11.4-2 15.2-5.5l-7.4-5.8c-2 1.4-4.7 2.2-7.8 2.2-6 0-11.1-4-12.9-9.5H3.4v5.9C7.2 42 14.9 47 24 47z"
                fill="#34A853"
              />
              <path
                d="M11.1 28.4c-.5-1.4-.8-3-.8-4.4s.3-3 .8-4.4v-5.9H3.4A23 23 0 0 0 1 24c0 3.7.9 7.2 2.4 10.3l7.7-5.9z"
                fill="#FBBC05"
              />
              <path
                d="M24 9.3c3.4 0 6.4 1.2 8.8 3.5l6.6-6.6C35.4 2.5 30.2 0 24 0 14.9 0 7.2 5 3.4 12.7l7.7 5.9C12.9 13.3 18 9.3 24 9.3z"
                fill="#EA4335"
              />
            </svg>
            {/* Green check overlay on the Google logo to match Figma's
                "connected" treatment — the logo gets its own status
                badge in the top-right. */}
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: "#10B981",
                border: "2px solid #fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3.5 8L6.5 11L12.5 5"
                  stroke="#fff"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
        </div>

        {renderTitle("Thanks for", "providing access")}

        <p
          style={{
            textAlign: "center",
            fontSize: 14,
            color: "#6b7280",
            margin: "12px 8px 16px",
            lineHeight: 1.5,
          }}
        >
          Your AI agent can now manage calls, calendars, and invites
          automatically.
        </p>

        {connectedEmail && (
          <div
            style={{
              textAlign: "center",
              background: "#f3f4f6",
              padding: "6px 12px",
              borderRadius: 8,
              fontSize: 13,
              color: "#374151",
              width: "fit-content",
              margin: "0 auto 14px",
            }}
          >
            {connectedEmail}
          </div>
        )}

        <div className={styles.content}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {SERVICES.map((s, idx) => {
              const granted = isGranted(s);
              const revealed = idx < revealedCount;
              const status = granted && revealed ? "check" : "pending";
              return renderServiceCard(s, status, idx);
            })}
          </div>

          {!allCriticalGranted && (
            <div
              style={{
                marginTop: 12,
                padding: "12px 14px",
                background: "#FEF3C7",
                border: "1px solid #F59E0B",
                borderRadius: 12,
                fontSize: 13,
                color: "#78350F",
                lineHeight: 1.5,
              }}
            >
              Some required permissions weren't granted. Your agent may
              not be able to manage calendar invites until you re-connect.
            </div>
          )}

          {justReturnedFromGoogle && allCriticalGranted && (
            <p
              style={{
                fontSize: 12,
                color: "#6b7280",
                textAlign: "center",
                marginTop: 12,
              }}
            >
              Continuing in a moment…
            </p>
          )}
        </div>
        <style>{`
          @keyframes googleAuthBadgePop {
            from { transform: scale(0.4); opacity: 0; }
            to   { transform: scale(1);   opacity: 1; }
          }
        `}</style>
      </section>
    );
  }

  const handleConnect = async () => {
    setError("");
    setBusy(true);
    if (busyTimeoutRef.current) clearTimeout(busyTimeoutRef.current);
    busyTimeoutRef.current = setTimeout(() => {
      setBusy(false);
      setError("Google sign-in took too long. Please try again.");
      busyTimeoutRef.current = null;
    }, 15000);
    try {
      const token = localStorage.getItem("token");
      const userId = decodeToken(token)?.id;
      if (!userId) {
        setError("Sign-in required. Please refresh.");
        setBusy(false);
        return;
      }
      const agentId = sessionStorage.getItem("SelectAgentId");

      // When running inside the React Native WebView, flag the request
      // so the calendar callback redirects via the com.rexptin://calendar
      // deep link instead of the plain web URL. App.tsx then posts
      // GOOGLE_CALENDAR_CONNECTED back and routes to returnToPath.
      const isMobile = !!window.ReactNativeWebView;

      const res = await axios.post(`${API_BASE_URL}/connect`, {
        userId,
        agentId,
        returnTo: returnToPath,
        isMobile,
      }, { timeout: 15000 });
      const authUrl = res?.data?.authUrl;
      if (!authUrl) {
        throw new Error("No authUrl in response");
      }
      // Mark that we initiated Google auth so when we come back the
      // wizard knows to render the success view + run auto-advance.
      // Keys are configurable so PostPurchase + GoLive each track
      // their own round-trip state without stomping on each other.
      // Do not overwrite an existing persisted wizard step. GoLive reaches
      // Google after Triage (internal step 4); replacing it with 1 caused a
      // refresh/back return to restart at Assign Number.
      if (!localStorage.getItem(lastStepKey)) {
        localStorage.setItem(lastStepKey, "1");
      }
      localStorage.setItem(expectReturnKey, "true");

      if (isMobile) {
        // Hand the URL to App.tsx so it opens in InAppBrowser. Same
        // OPEN_GOOGLE_AUTH contract Contacts uses — App.tsx routes both
        // to handleGoogleSignIn which calls InAppBrowser.openAuth.
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "OPEN_GOOGLE_AUTH", authUrl, flow: "calendar" }),
        );
      } else {
        window.location.href = authUrl;
      }
    } catch (err) {
      console.error("[GoogleAuthStep] connect failed:", err);
      setError("Could not start Google sign-in. Please try again.");
      setBusy(false);
      if (busyTimeoutRef.current) {
        clearTimeout(busyTimeoutRef.current);
        busyTimeoutRef.current = null;
      }
    }
  };

  // ── Initial / disconnected view — service cards with red ✕ badges ──
  return (
    /*
       paddingTop here is scoped to GoogleAuthStep only — StepShell.module.css
       has `.step { padding: 0px 20px 140px; }` (zero top) which other steps
       rely on. Adding it inline gives the Google logo + 2-line title proper
       breathing room from the top of the wizard chrome without bleeding
       into other wizard screens.
    */
    <section className={styles.step} style={{ paddingTop: 32 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: 8,
          marginBottom: 18,
        }}
      >
        <div
          style={{
            width: 76,
            height: 76,
            borderRadius: "50%",
            background: "#fff",
            border: "1px solid #E0E5F2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
            <path
              d="M46 24.5c0-1.6-.1-3.2-.4-4.7H24v8.9h12.4c-.5 2.9-2.1 5.3-4.6 7v5.8h7.4c4.3-4 6.8-9.9 6.8-17z"
              fill="#4285F4"
            />
            <path
              d="M24 47c6.2 0 11.4-2 15.2-5.5l-7.4-5.8c-2 1.4-4.7 2.2-7.8 2.2-6 0-11.1-4-12.9-9.5H3.4v5.9C7.2 42 14.9 47 24 47z"
              fill="#34A853"
            />
            <path
              d="M11.1 28.4c-.5-1.4-.8-3-.8-4.4s.3-3 .8-4.4v-5.9H3.4A23 23 0 0 0 1 24c0 3.7.9 7.2 2.4 10.3l7.7-5.9z"
              fill="#FBBC05"
            />
            <path
              d="M24 9.3c3.4 0 6.4 1.2 8.8 3.5l6.6-6.6C35.4 2.5 30.2 0 24 0 14.9 0 7.2 5 3.4 12.7l7.7 5.9C12.9 13.3 18 9.3 24 9.3z"
              fill="#EA4335"
            />
          </svg>
        </div>
      </div>

      {renderTitle("Let your agent", "work with Google")}

      <p
        style={{
          textAlign: "center",
          fontSize: 14,
          color: "#6b7280",
          margin: "12px 8px 18px",
          lineHeight: 1.55,
        }}
      >
        <b style={{ color: "#24252c", fontWeight: 700 }}>Connect Google</b>{" "}
        so your AI agent can manage calendars, recognize callers, and{" "}
        <b style={{ color: "#24252c", fontWeight: 700 }}>send invites</b>{" "}
        automatically.
      </p>

      <div className={styles.content}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {SERVICES.map((s, idx) => renderServiceCard(s, "cross", idx))}
        </div>

        {error && (
          <p style={{ color: "#dc2626", fontSize: 13, margin: 0 }}>{error}</p>
        )}
      </div>

      <div className={styles.ctaBar}>
        <div className={styles.ctaInner}>
          <AnimatedButton
            label={busy ? "Opening Google…" : "Continue"}
            onClick={handleConnect}
            disabled={busy}
            positionType="relative"
          />
          {/*
             Optional skip link — only renders when the host wizard
             explicitly opts in via `showSkipLink={true}`. PostPurchase
             doesn't pass that prop (Google connect there is mandatory),
             so even though it sends a generic onSkip via StepComponent
             the link stays hidden. GoLive passes BOTH.
          */}
          {showSkipLink && typeof onSkip === "function" && (
            <button
              type="button"
              onClick={onSkip}
              disabled={busy}
              style={{
                display: "block",
                margin: "0 auto",
                background: "transparent",
                border: "none",
                padding: "6px 12px",
                fontSize: 14,
                fontWeight: 600,
                color: "#24252c",
                textDecoration: "underline",
                textUnderlineOffset: 3,
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.5 : 1,
                fontFamily: "inherit",
              }}
            >
              Skip
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
